import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX, Monitor, Zap, Save, RotateCcw, Maximize, Minimize, Globe } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

interface AlbionGameSettings {
    soundEnabled: boolean;
    soundVolume: number;
    musicVolume: number;
    crtEffects: boolean;
    animationSpeed: number;
    autoSave: boolean;
    debugMode: boolean;
    fullscreenMode: boolean;
}

interface GameSettingsProps {
    isOpen: boolean;
    onClose: () => void;
}

export const GameSettings: React.FC<GameSettingsProps> = ({ isOpen, onClose }) => {
    const { t, language, changeLanguage } = useLanguage();
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [settings, setSettings] = useState<AlbionGameSettings>(() => {
        // Charger les paramètres sauvegardés
        const saved = localStorage.getItem('ALBION_GAME_SETTINGS');
        if (saved) {
            try {
                const parsedSettings = JSON.parse(saved);
                // S'assurer que fullscreenMode est toujours défini
                const baseSettings = {
                    soundEnabled: true,
                    soundVolume: 0.7,
                    musicVolume: 0.7,
                    crtEffects: true,
                    animationSpeed: 1,
                    autoSave: false,
                    debugMode: false,
                    fullscreenMode: true // Valeur par défaut
                };
                
                return {
                    ...baseSettings,
                    ...parsedSettings, // Écraser avec les valeurs sauvegardées
                    // S'assurer qu'undefined devient true
                    fullscreenMode: parsedSettings.fullscreenMode !== false
                };
            } catch (e) {}
        }
        // Paramètres par défaut
        return {
            soundEnabled: true,
            soundVolume: 0.7,
            musicVolume: 0.7,
            crtEffects: true,
            animationSpeed: 1,
            autoSave: false, // Désactivé par défaut pour le développement
            debugMode: false,
            fullscreenMode: true // Plein écran par défaut
        };
    });

    const [isInitialized, setIsInitialized] = useState(false);

    // Marquer comme initialisé après le premier render
    useEffect(() => {
        setIsInitialized(true);
    }, []);

    // Sauvegarder les paramètres quand ils changent
    useEffect(() => {
        localStorage.setItem('ALBION_GAME_SETTINGS', JSON.stringify(settings));
        
        // Émettre un événement pour notifier les autres composants SEULEMENT si initialisé
        if (isInitialized) {
            console.log('GameSettings - Emitting settings change event', settings);
            window.dispatchEvent(new CustomEvent('GAME_SETTINGS_CHANGED', { detail: settings }));
        }
    }, [settings, isInitialized]);

    const updateSetting = <K extends keyof AlbionGameSettings>(key: K, value: AlbionGameSettings[K]) => {
        setSettings(prev => {
            const newSettings = { ...prev, [key]: value };
            
            // Le plein écran est maintenant géré globalement par App.tsx
            // Plus besoin d'appliquer ici
            
            return newSettings;
        });
    };

    const resetToDefaults = () => {
        const defaultSettings: AlbionGameSettings = {
            soundEnabled: true,
            soundVolume: 0.7,
            musicVolume: 0.7,
            crtEffects: true,
            animationSpeed: 1,
            autoSave: false,
            debugMode: false,
            fullscreenMode: true
        };
        setSettings(defaultSettings);
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center backdrop-blur-sm">
                <div className="relative w-full max-w-2xl bg-slate-800 border border-slate-600 shadow-2xl shadow-black/50 flex flex-col p-1 animate-in zoom-in-95 duration-200">
                {/* Barre de titre */}
                <div className="bg-slate-900 text-cyan-400 px-4 py-2 text-sm font-bold flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                        <Monitor size={16} />
                        <span className="uppercase tracking-wider">{t('settings.title')}</span>
                    </div>
                    <button
                        className="bg-slate-700 text-slate-300 w-6 h-6 flex items-center justify-center text-xs border border-slate-600 font-bold hover:bg-slate-600 active:bg-slate-500"
                        onClick={onClose}
                    >
                        X
                    </button>
                </div>

                {/* Contenu des paramètres */}
                <div className="p-6 space-y-6">
                    {/* Section Audio */}
                    <div className="space-y-4">
                        <h3 className="text-cyan-400 font-bold text-sm uppercase tracking-wider border-b border-slate-600 pb-2">
                            {t('settings.audio')}
                        </h3>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {settings.soundEnabled ? <Volume2 size={16} className="text-cyan-400" /> : <VolumeX size={16} className="text-slate-500" />}
                                <span className="text-slate-300 text-sm">{t('settings.soundEnabled')}</span>
                            </div>
                            <button
                                onClick={() => updateSetting('soundEnabled', !settings.soundEnabled)}
                                className={`w-12 h-6 rounded-full border transition-colors ${
                                    settings.soundEnabled 
                                        ? 'bg-cyan-600 border-cyan-500' 
                                        : 'bg-slate-700 border-slate-600'
                                }`}
                            >
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                                    settings.soundEnabled ? 'translate-x-7' : 'translate-x-1'
                                }`} />
                            </button>
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-slate-300 text-sm">{t('settings.soundVolume')}</span>
                            <div className="flex items-center gap-3 w-48">
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={settings.soundVolume}
                                    onChange={(e) => updateSetting('soundVolume', parseFloat(e.target.value))}
                                    disabled={!settings.soundEnabled}
                                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-slate-300 text-sm">{t('settings.musicVolume')}</span>
                            <div className="flex items-center gap-3 w-48">
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={settings.musicVolume}
                                    onChange={(e) => updateSetting('musicVolume', parseFloat(e.target.value))}
                                    disabled={!settings.soundEnabled}
                                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section Affichage */}
                    <div className="space-y-4">
                        <h3 className="text-cyan-400 font-bold text-sm uppercase tracking-wider border-b border-slate-600 pb-2">
                            {t('settings.video')}
                        </h3>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Monitor size={16} className="text-cyan-400" />
                                <span className="text-slate-300 text-sm">{t('settings.crtEffects')}</span>
                            </div>
                            <button
                                onClick={() => updateSetting('crtEffects', !settings.crtEffects)}
                                className={`w-12 h-6 rounded-full border transition-colors ${
                                    settings.crtEffects 
                                        ? 'bg-cyan-600 border-cyan-500' 
                                        : 'bg-slate-700 border-slate-600'
                                }`}
                            >
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                                    settings.crtEffects ? 'translate-x-7' : 'translate-x-1'
                                }`} />
                            </button>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {settings.fullscreenMode ? <Maximize size={16} className="text-cyan-400" /> : <Minimize size={16} className="text-cyan-400" />}
                                <span className="text-slate-300 text-sm">{t('settings.fullscreenMode')}</span>
                            </div>
                            <button
                                onClick={() => updateSetting('fullscreenMode', !settings.fullscreenMode)}
                                className={`w-12 h-6 rounded-full border transition-colors ${
                                    settings.fullscreenMode 
                                        ? 'bg-cyan-600 border-cyan-500' 
                                        : 'bg-slate-700 border-slate-600'
                                }`}
                            >
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                                    settings.fullscreenMode ? 'translate-x-7' : 'translate-x-1'
                                }`} />
                            </button>
                        </div>
                    </div>

                    {/* Section Système */}
                    <div className="space-y-4">
                        <h3 className="text-cyan-400 font-bold text-sm uppercase tracking-wider border-b border-slate-600 pb-2">
                            {t('settings.general')}
                        </h3>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Globe size={16} className="text-cyan-400" />
                                <span className="text-slate-300 text-sm">{t('settings.language')}</span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => changeLanguage('fr')}
                                    className={`px-4 py-1 text-sm border transition-colors ${
                                        language === 'fr'
                                            ? 'bg-cyan-600 border-cyan-500 text-white'
                                            : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                                    }`}
                                >
                                    {t('settings.languages.fr')}
                                </button>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    className={`px-4 py-1 text-sm border transition-colors ${
                                        language === 'en'
                                            ? 'bg-cyan-600 border-cyan-500 text-white'
                                            : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                                    }`}
                                >
                                    {t('settings.languages.en')}
                                </button>
                            </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Save size={16} className="text-cyan-400" />
                                <span className="text-slate-300 text-sm">{t('settings.autoSave')}</span>
                            </div>
                            <button
                                onClick={() => updateSetting('autoSave', !settings.autoSave)}
                                className={`w-12 h-6 rounded-full border transition-colors ${
                                    settings.autoSave 
                                        ? 'bg-cyan-600 border-cyan-500' 
                                        : 'bg-slate-700 border-slate-600'
                                }`}
                            >
                                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                                    settings.autoSave ? 'translate-x-7' : 'translate-x-1'
                                }`} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Boutons d'action */}
                <div className="border-t border-slate-600 p-4 flex justify-between items-center">
                    <button
                        onClick={resetToDefaults}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600 transition-colors text-sm"
                    >
                        <RotateCcw size={14} />
                        {t('settings.reset')}
                    </button>

                    <div className="flex items-center gap-3">
                        {/* Bouton réinitialisation démo - discret */}
                        <button
                            onClick={() => setShowResetConfirm(true)}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 text-slate-400 border border-slate-600 hover:bg-slate-600 hover:text-slate-300 transition-colors text-xs"
                            title={t('settings.demo.resetButton')}
                        >
                            <RotateCcw size={12} />
                            <span>{t('settings.demo.resetDemo')}</span>
                        </button>
                        
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-cyan-700 text-cyan-100 border border-cyan-600 hover:bg-cyan-600 transition-colors text-sm font-bold"
                        >
                            {t('settings.apply')}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        {/* Fenêtre de confirmation diégétique */}
        {showResetConfirm && (
            <div className="fixed inset-0 bg-black/80 z-[10000] flex items-center justify-center backdrop-blur-sm">
                <div className="relative w-full max-w-md bg-slate-800 border-2 border-red-600 shadow-2xl shadow-red-900/50 flex flex-col p-1 animate-in zoom-in-95 duration-200">
                    {/* Barre de titre */}
                    <div className="bg-red-900 text-red-100 px-4 py-2 text-sm font-bold flex justify-between items-center mb-1">
                        <div className="flex items-center gap-2">
                            <RotateCcw size={16} />
                            <span className="uppercase tracking-wider">⚠ {t('settings.demo.confirmTitle')}</span>
                        </div>
                    </div>

                    {/* Contenu */}
                    <div className="p-6 space-y-4">
                        <p className="text-slate-200 text-sm leading-relaxed">
                            {t('settings.demo.confirmReset')}
                        </p>
                        
                        <div className="bg-red-950/50 border border-red-900/50 p-3 rounded">
                            <p className="text-red-300 text-xs font-mono">
                                {t('settings.demo.confirmDetails')}
                            </p>
                        </div>
                    </div>

                    {/* Boutons */}
                    <div className="border-t border-red-900/50 p-4 flex gap-3">
                        <button
                            onClick={() => setShowResetConfirm(false)}
                            className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600 transition-colors text-sm font-bold"
                        >
                            {t('settings.demo.cancel')}
                        </button>
                        
                        <button
                            onClick={() => {
                                // Supprimer TOUTES les données localStorage
                                localStorage.clear();
                                
                                // Recharger la page pour revenir à l'écran de sélection de langue
                                window.location.reload();
                            }}
                            className="flex-1 px-4 py-2 bg-red-700 text-red-100 border border-red-600 hover:bg-red-600 transition-colors text-sm font-bold"
                        >
                            {t('settings.demo.confirmButton')}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </>
    );
};